import type { RiskForm } from './risk-form'
import type { QualityRisk } from '~/gql/quality/quality'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { RISK_STATUS_KEYS, SCORE_KEYS } from './quality-labels'
import QualityField, { FIELD_CLASS } from './QualityField'
import { isRiskFormValid, riskForm } from './risk-form'

type IProps = {
  /** Le risque à revoir ; absent, on en crée un. */
  risk?: QualityRisk
  busy: boolean
  onCancel: () => void
  onSave: (form: RiskForm) => void
}

/** Identifier ou revoir un risque qualité (#328) : sa cote, la mesure qui le réduit, qui la porte. */
function QualityRiskDialog({ risk, busy, onCancel, onSave }: IProps) {
  const { t } = useTranslation()
  const [form, setForm] = useState(() => riskForm(risk))
  const set = <K extends keyof RiskForm>(key: K, value: RiskForm[K]) => setForm(current => ({ ...current, [key]: value }))
  const scoreSelect = (id: string, key: 'likelihood' | 'impact') => (
    <select id={id} value={form[key]} onChange={e => set(key, Number(e.target.value))} className={`h-10 ${FIELD_CLASS}`}>
      {Object.entries(SCORE_KEYS).map(([value, label]) => <option key={value} value={value}>{`${value} — ${t(label)}`}</option>)}
    </select>
  )

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (isRiskFormValid(form)) onSave(form)
  }

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{risk ? risk.title : t('quality.risks.new.title')}</DialogTitle>
            <DialogDescription>{t('quality.risks.severityHelp', { severity: form.likelihood * form.impact })}</DialogDescription>
          </DialogHeader>
          <QualityField id="riskTitle" label={t('quality.column.title')} required>
            <Input id="riskTitle" required maxLength={200} value={form.title} onChange={e => set('title', e.target.value)} />
          </QualityField>
          <QualityField id="riskDescription" label={t('quality.risks.column.description')} required>
            <textarea id="riskDescription" required rows={3} maxLength={5000} value={form.description} onChange={e => set('description', e.target.value)} className={`py-2 ${FIELD_CLASS}`} />
          </QualityField>
          <div className="grid gap-4 sm:grid-cols-2">
            <QualityField id="riskLikelihood" label={t('quality.risks.column.likelihood')}>{scoreSelect('riskLikelihood', 'likelihood')}</QualityField>
            <QualityField id="riskImpact" label={t('quality.risks.column.impact')}>{scoreSelect('riskImpact', 'impact')}</QualityField>
          </div>
          <QualityField id="riskMitigation" label={t('quality.risks.column.mitigation')}>
            <textarea id="riskMitigation" rows={3} maxLength={5000} value={form.mitigation} onChange={e => set('mitigation', e.target.value)} className={`py-2 ${FIELD_CLASS}`} />
          </QualityField>
          <div className="grid gap-4 sm:grid-cols-2">
            <QualityField id="riskOwner" label={t('quality.column.owner')}>
              <Input id="riskOwner" maxLength={150} value={form.owner} onChange={e => set('owner', e.target.value)} />
            </QualityField>
            {risk && (
              <QualityField id="riskStatus" label={t('quality.column.status')}>
                <select id="riskStatus" value={form.status} onChange={e => set('status', e.target.value as RiskForm['status'])} className={`h-10 ${FIELD_CLASS}`}>
                  {Object.entries(RISK_STATUS_KEYS).map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}
                </select>
              </QualityField>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={!isRiskFormValid(form) || busy}>{risk ? t('common.save') : t('quality.risks.new.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default QualityRiskDialog
